// src/login/login.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './login.css';

const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'https://api.jmkinfosoft.com';

const Login = () => {
  const [mobileNumber, setMobileNumber] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  // =========================================================
  // FORGOT PASSWORD STATES - ADDED
  // =========================================================

  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotStep, setForgotStep] = useState(1);

  const [forgotMobile, setForgotMobile] = useState('');
  const [forgotOtp, setForgotOtp] = useState('');

  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

  const [forgotLoading, setForgotLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  const [resendTimer, setResendTimer] = useState(0);

  const forgotErrorRef = useRef(null);

  // Clear any existing session when login page loads
  useEffect(() => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userMobile');
    localStorage.removeItem('userName');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userRole');
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    sessionStorage.clear();
  }, []);

  // =========================================================
  // FORGOT PASSWORD TIMER - ADDED
  // =========================================================

  useEffect(() => {
    if (resendTimer <= 0) return;

    const timer = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [resendTimer]);

  // =========================================================
  // AUTO SCROLL ERROR INTO VIEW - ADDED
  // =========================================================

  useEffect(() => {
    if (error && forgotErrorRef.current) {
      setTimeout(() => {
        forgotErrorRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    }
  }, [error]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!mobileNumber || !password) {
      setError('Please fill in all fields');
      return;
    }

    const mobileRegex = /^[0-9]{10}$/;
    if (!mobileRegex.test(mobileNumber)) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);

    try {
      let response;
      const mobile = mobileNumber;
      const pass = password;

      // First try candidate login
      try {
        response = await axios.post(`${API_BASE_URL}/api/auth/login`, {
          mobile: mobile,
          password: pass
        });
      } catch (candidateError) {
        // If candidate login fails, try admin login
        if (
          candidateError.response?.status === 403 ||
          candidateError.response?.status === 401
        ) {
          response = await axios.post(
            `${API_BASE_URL}/api/auth/admin/login`,
            {
              mobile: mobile,
              password: pass
            }
          );
        } else {
          throw candidateError;
        }
      }

      if (response.data && response.data.success) {
        const userData = response.data.data;
        const user = userData.user || userData;

        console.log('✅ Login Response:', userData);
        console.log('👤 User:', user);
        console.log('🆔 User ID:', user.id);

        // Store token and ALL user data
        localStorage.setItem('token', userData.token);
        localStorage.setItem('userId', user.id);
        localStorage.setItem('isLoggedIn', 'true');
        localStorage.setItem(
          'userMobile',
          user.mobile || mobileNumber
        );
        localStorage.setItem(
          'userName',
          user.name || 'User'
        );
        localStorage.setItem(
          'userEmail',
          user.email || ''
        );
        localStorage.setItem(
          'userRole',
          user.role || 'candidate'
        );

        if (rememberMe) {
          localStorage.setItem('rememberMe', 'true');
        }

        // Debug: Check if userId is stored
        console.log('📦 Stored in localStorage:');
        console.log(
          '  - userId:',
          localStorage.getItem('userId')
        );
        console.log(
          '  - userRole:',
          localStorage.getItem('userRole')
        );
        console.log(
          '  - userName:',
          localStorage.getItem('userName')
        );
        console.log(
          '  - token:',
          localStorage.getItem('token')
            ? '✅ Present'
            : '❌ Missing'
        );

        // Redirect based on role
        const role = user.role || 'candidate';
        redirectUser(role);
        return;
      }

      setError('Invalid credentials');
    } catch (error) {
      console.error('Login error:', error);

      if (error.code === 'ERR_NETWORK') {
        setError(
          'Cannot connect to server. Please check if backend is running.'
        );
      } else if (error.response?.status === 404) {
        setError(
          'Login endpoint not found. Please check backend configuration.'
        );
      } else if (error.response?.status === 401) {
        setError(
          error.response?.data?.message ||
            'Invalid mobile number or password'
        );
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const redirectUser = (role) => {
    switch (role) {
      case 'super_admin':
        navigate('/super-admin', { replace: true });
        break;

      case 'admin':
        navigate('/admin/view-requirement', {
          replace: true
        });
        break;

      case 'external_admin':
        navigate('/external-admin/view-requirement', {
          replace: true
        });
        break;

      case 'candidate':
      default:
        navigate('/user/view-requirement', {
          replace: true
        });
    }
  };

  // =========================================================
  // FORGOT PASSWORD - OPEN
  // =========================================================

  const handleForgotPassword = (e) => {
    e.preventDefault();

    setError('');

    setShowForgotPassword(true);
    setForgotStep(1);

    setForgotMobile('');
    setForgotOtp('');
    setNewPassword('');
    setConfirmNewPassword('');

    setResendTimer(0);
  };

  // =========================================================
  // FORGOT PASSWORD - CLOSE
  // =========================================================

  const closeForgotPassword = () => {
    setShowForgotPassword(false);

    setForgotStep(1);

    setForgotMobile('');
    setForgotOtp('');
    setNewPassword('');
    setConfirmNewPassword('');

    setResendTimer(0);
    setError('');
  };

  // =========================================================
  // FORGOT MOBILE CHANGE
  // =========================================================

  const handleForgotMobileChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '');

    if (raw.length <= 10) {
      setForgotMobile(raw);
    }
  };

  // =========================================================
  // SEND WHATSAPP OTP
  // =========================================================

  const handleSendForgotOtp = async (e) => {
    e.preventDefault();

    setError('');

    if (!forgotMobile) {
      setError('Please enter your registered mobile number');
      return;
    }

    const mobileRegex = /^[0-9]{10}$/;

    if (!mobileRegex.test(forgotMobile)) {
      setError(
        'Please enter a valid 10-digit mobile number'
      );
      return;
    }

    setForgotLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/otp/request`,
        {
          mobile: forgotMobile,
          purpose: 'reset_password'
        }
      );

      console.log(
        'Forgot Password OTP Response:',
        response.data
      );

      if (response.data && response.data.success) {
        setForgotStep(2);

        setForgotOtp('');

        setResendTimer(60);

        setError('');

        if (
          response.data.data &&
          response.data.data.debugOtp
        ) {
          console.log(
            '🔐 Debug OTP:',
            response.data.data.debugOtp
          );
        }
      } else {
        setError(
          response.data?.message ||
            'Failed to send OTP. Please try again.'
        );
      }
    } catch (error) {
      console.error(
        'Forgot Password OTP error:',
        error
      );

      setError(
        error.response?.data?.message ||
          'Failed to send OTP. Please try again.'
      );
    } finally {
      setForgotLoading(false);
    }
  };

  // =========================================================
  // RESEND OTP
  // =========================================================

  const handleResendOtp = async () => {
    if (resendTimer > 0 || resendLoading) {
      return;
    }

    setError('');
    setResendLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/otp/request`,
        {
          mobile: forgotMobile,
          purpose: 'reset_password'
        }
      );

      console.log(
        'Resend OTP Response:',
        response.data
      );

      if (response.data && response.data.success) {
        setResendTimer(60);
        setForgotOtp('');

        if (
          response.data.data &&
          response.data.data.debugOtp
        ) {
          console.log(
            '🔐 Debug OTP:',
            response.data.data.debugOtp
          );
        }
      } else {
        setError(
          response.data?.message ||
            'Failed to resend OTP.'
        );
      }
    } catch (error) {
      console.error('Resend OTP error:', error);

      setError(
        error.response?.data?.message ||
          'Failed to resend OTP. Please try again.'
      );
    } finally {
      setResendLoading(false);
    }
  };

  // =========================================================
  // OTP CHANGE
  // =========================================================

  const handleForgotOtpChange = (e) => {
    const value = e.target.value
      .replace(/\D/g, '')
      .slice(0, 6);

    setForgotOtp(value);
  };

  // =========================================================
  // PASSWORD CHANGE
  // =========================================================

  const handleNewPasswordChange = (e) => {
    setNewPassword(e.target.value);
  };

  const handleConfirmNewPasswordChange = (e) => {
    setConfirmNewPassword(e.target.value);
  };

  // =========================================================
  // VERIFY OTP + RESET PASSWORD
  // =========================================================

  const handleResetPassword = async (e) => {
    e.preventDefault();

    setError('');

    if (!forgotOtp) {
      setError('Please enter the OTP');
      return;
    }

    if (forgotOtp.length !== 6) {
      setError('Please enter the 6-digit OTP');
      return;
    }

    if (!newPassword) {
      setError('Please enter your new password');
      return;
    }

    /*
     * Backend requires minimum 8 characters
     */
    if (newPassword.length < 8) {
      setError(
        'New password must be at least 8 characters'
      );
      return;
    }

    if (!confirmNewPassword) {
      setError('Please confirm your new password');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setError(
        'New password and confirm password do not match'
      );
      return;
    }

    setForgotLoading(true);

    try {
      /*
       * IMPORTANT:
       *
       * Backend expects "code", not "otp".
       *
       * Backend also expects password in the same request.
       *
       * There is no separate reset-password API.
       */

      const response = await axios.post(
        `${API_BASE_URL}/api/auth/otp/verify`,
        {
          mobile: forgotMobile,
          code: forgotOtp,
          password: newPassword,
          name: '',
          email: '',
          purpose: 'reset_password'
        }
      );

      console.log(
        'Password Reset Response:',
        response.data
      );

      if (response.data && response.data.success) {
        setForgotStep(3);

        setError('');

        setForgotOtp('');
        setNewPassword('');
        setConfirmNewPassword('');

        setResendTimer(0);
      } else {
        setError(
          response.data?.message ||
            'Failed to reset password.'
        );
      }
    } catch (error) {
      console.error(
        'Reset password error:',
        error
      );

      setError(
        error.response?.data?.message ||
          'Invalid OTP or failed to reset password.'
      );
    } finally {
      setForgotLoading(false);
    }
  };

  const handleRegister = () => {
    navigate('/register');
  };

  const formatMobileNumber = (value) => {
    const cleaned = value.replace(/\D/g, '');

    if (cleaned.length <= 5) return cleaned;

    return `${cleaned.slice(0, 5)} ${cleaned.slice(
      5,
      10
    )}`;
  };

  const handleMobileChange = (e) => {
    const raw = e.target.value.replace(/\s/g, '');

    if (raw.length <= 10) {
      setMobileNumber(raw);
    }
  };

  // =========================================================
  // MAIN LOGIN UI
  // =========================================================

  return (
    <div className="login-container">
      <div className="login-card">

        {/* ================================================= */}
        {/* NORMAL LOGIN */}
        {/* ================================================= */}

        {!showForgotPassword && (
          <>
            <div className="login-header">
              <h1>Welcome Back</h1>
              <p>Sign in to your account</p>
            </div>

            {error && (
              <div className="error-message">
                <i className="fas fa-exclamation-circle"></i>
                <span>{error}</span>
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              className="login-form"
            >
              <div className="form-group">
                <label htmlFor="mobile">
                  <i className="fas fa-phone"></i>
                  Mobile Number
                </label>

                <input
                  type="tel"
                  id="mobile"
                  placeholder="00000 00000"
                  value={formatMobileNumber(
                    mobileNumber
                  )}
                  onChange={handleMobileChange}
                  disabled={isLoading}
                  autoComplete="tel"
                  required
                  maxLength="11"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">
                  <i className="fas fa-lock"></i>
                  Password
                </label>

                <div className="password-wrapper">
                  <input
                    type={
                      showPassword
                        ? 'text'
                        : 'password'
                    }
                    id="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) =>
                      setPassword(e.target.value)
                    }
                    disabled={isLoading}
                    autoComplete="current-password"
                    required
                  />

                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() =>
                      setShowPassword(
                        !showPassword
                      )
                    }
                    tabIndex="-1"
                  >
                    <i
                      className={`fas ${
                        showPassword
                          ? 'fa-eye-slash'
                          : 'fa-eye'
                      }`}
                    ></i>
                  </button>
                </div>
              </div>

              {/* Remember Me + Forgot Password */}

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '18px'
                }}
              >
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '7px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) =>
                      setRememberMe(
                        e.target.checked
                      )
                    }
                    disabled={isLoading}
                  />

                  Remember Me
                </label>

                <button
                  type="button"
                  onClick={
                    handleForgotPassword
                  }
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: '#007bff',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Forgot Password?
                </button>
              </div>

              <button
                type="submit"
                className="login-btn"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner"></span>
                    Signing in...
                  </>
                ) : (
                  <>
                    <i className="fas fa-sign-in-alt"></i>
                    Sign In
                  </>
                )}
              </button>
            </form>

            <div className="login-register-section">
              <span className="register-divider">
                or
              </span>

              <button
                type="button"
                className="register-btn"
                onClick={handleRegister}
              >
                <i className="fas fa-user-plus"></i>
                Register as Candidate
              </button>
            </div>
          </>
        )}

        {/* ================================================= */}
        {/* FORGOT PASSWORD */}
        {/* ================================================= */}

        {showForgotPassword && (
          <>
            <div className="login-header">
              <h1>
                {forgotStep === 1 &&
                  'Forgot Password'}

                {forgotStep === 2 &&
                  'Verify OTP'}

                {forgotStep === 3 &&
                  'Password Reset'}
              </h1>

              <p>
                {forgotStep === 1 &&
                  'Enter your registered mobile number'}

                {forgotStep === 2 &&
                  'Enter the WhatsApp OTP and set your new password'}

                {forgotStep === 3 &&
                  'Your password has been reset successfully'}
              </p>
            </div>

            {/* Error */}

            {error && (
              <div
                ref={forgotErrorRef}
                className="error-message"
              >
                <i className="fas fa-exclamation-circle"></i>
                <span>{error}</span>
              </div>
            )}

            {/* ================================================= */}
            {/* STEP 1 - MOBILE */}
            {/* ================================================= */}

            {forgotStep === 1 && (
              <form
                onSubmit={handleSendForgotOtp}
                className="login-form"
              >
                <div className="form-group">
                  <label htmlFor="forgotMobile">
                    <i className="fas fa-phone"></i>
                    Registered Mobile Number
                  </label>

                  <input
                    type="tel"
                    id="forgotMobile"
                    placeholder="00000 00000"
                    value={formatMobileNumber(
                      forgotMobile
                    )}
                    onChange={
                      handleForgotMobileChange
                    }
                    disabled={forgotLoading}
                    autoComplete="tel"
                    required
                    maxLength="11"
                  />

                  <small
                    style={{
                      display: 'block',
                      marginTop: '7px',
                      color: '#666'
                    }}
                  >
                    OTP will be sent to your
                    registered WhatsApp number.
                  </small>
                </div>

                <button
                  type="submit"
                  className="login-btn"
                  disabled={forgotLoading}
                >
                  {forgotLoading ? (
                    <>
                      <span className="spinner"></span>
                      Sending OTP...
                    </>
                  ) : (
                    <>
                      <i className="fab fa-whatsapp"></i>
                      Send WhatsApp OTP
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={
                    closeForgotPassword
                  }
                  style={{
                    width: '100%',
                    marginTop: '12px',
                    padding: '10px',
                    background: 'transparent',
                    border: 'none',
                    color: '#007bff',
                    cursor: 'pointer'
                  }}
                >
                  ← Back to Login
                </button>
              </form>
            )}

            {/* ================================================= */}
            {/* STEP 2 - OTP + NEW PASSWORD */}
            {/* ================================================= */}

            {forgotStep === 2 && (
              <form
                onSubmit={handleResetPassword}
                className="login-form"
              >
                <div
                  style={{
                    background: '#f5f9ff',
                    padding: '10px 12px',
                    borderRadius: '6px',
                    marginBottom: '15px',
                    fontSize: '14px'
                  }}
                >
                  OTP sent to{' '}
                  <strong>
                    {formatMobileNumber(
                      forgotMobile
                    )}
                  </strong>
                </div>

                {/* OTP */}

                <div className="form-group">
                  <label htmlFor="forgotOtp">
                    <i className="fas fa-key"></i>
                    WhatsApp OTP
                  </label>

                  <input
                    type="text"
                    id="forgotOtp"
                    placeholder="Enter 6-digit OTP"
                    value={forgotOtp}
                    onChange={
                      handleForgotOtpChange
                    }
                    disabled={forgotLoading}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength="6"
                    required
                  />
                </div>

                {/* New Password */}

                <div className="form-group">
                  <label htmlFor="newPassword">
                    <i className="fas fa-lock"></i>
                    New Password
                  </label>

                  <div className="password-wrapper">
                    <input
                      type={
                        showNewPassword
                          ? 'text'
                          : 'password'
                      }
                      id="newPassword"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={
                        handleNewPasswordChange
                      }
                      disabled={forgotLoading}
                      autoComplete="new-password"
                      required
                    />

                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() =>
                        setShowNewPassword(
                          !showNewPassword
                        )
                      }
                      tabIndex="-1"
                    >
                      <i
                        className={`fas ${
                          showNewPassword
                            ? 'fa-eye-slash'
                            : 'fa-eye'
                        }`}
                      ></i>
                    </button>
                  </div>

                  <small
                    style={{
                      display: 'block',
                      marginTop: '6px',
                      color: '#666'
                    }}
                  >
                    Minimum 8 characters
                  </small>
                </div>

                {/* Confirm Password */}

                <div className="form-group">
                  <label htmlFor="confirmNewPassword">
                    <i className="fas fa-lock"></i>
                    Confirm New Password
                  </label>

                  <div className="password-wrapper">
                    <input
                      type={
                        showConfirmNewPassword
                          ? 'text'
                          : 'password'
                      }
                      id="confirmNewPassword"
                      placeholder="Confirm new password"
                      value={
                        confirmNewPassword
                      }
                      onChange={
                        handleConfirmNewPasswordChange
                      }
                      disabled={forgotLoading}
                      autoComplete="new-password"
                      required
                    />

                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() =>
                        setShowConfirmNewPassword(
                          !showConfirmNewPassword
                        )
                      }
                      tabIndex="-1"
                    >
                      <i
                        className={`fas ${
                          showConfirmNewPassword
                            ? 'fa-eye-slash'
                            : 'fa-eye'
                        }`}
                      ></i>
                    </button>
                  </div>
                </div>

                {/* Reset */}

                <button
                  type="submit"
                  className="login-btn"
                  disabled={forgotLoading}
                >
                  {forgotLoading ? (
                    <>
                      <span className="spinner"></span>
                      Resetting Password...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-key"></i>
                      Verify OTP & Reset Password
                    </>
                  )}
                </button>

                {/* Resend */}

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '15px'
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setForgotStep(1);
                      setForgotOtp('');
                      setError('');
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#007bff',
                      cursor: 'pointer',
                      padding: 0
                    }}
                  >
                    Change Number
                  </button>

                  <button
                    type="button"
                    onClick={
                      handleResendOtp
                    }
                    disabled={
                      resendTimer > 0 ||
                      resendLoading
                    }
                    style={{
                      background: 'none',
                      border: 'none',
                      color:
                        resendTimer > 0
                          ? '#999'
                          : '#007bff',
                      cursor:
                        resendTimer > 0
                          ? 'not-allowed'
                          : 'pointer',
                      padding: 0
                    }}
                  >
                    {resendTimer > 0
                      ? `Resend OTP in ${resendTimer}s`
                      : resendLoading
                      ? 'Sending...'
                      : 'Resend OTP'}
                  </button>
                </div>

                <button
                  type="button"
                  onClick={
                    closeForgotPassword
                  }
                  style={{
                    width: '100%',
                    marginTop: '12px',
                    padding: '10px',
                    background: 'transparent',
                    border: 'none',
                    color: '#007bff',
                    cursor: 'pointer'
                  }}
                >
                  ← Back to Login
                </button>
              </form>
            )}

            {/* ================================================= */}
            {/* STEP 3 - SUCCESS */}
            {/* ================================================= */}

            {forgotStep === 3 && (
              <div
                style={{
                  textAlign: 'center',
                  padding: '20px 5px'
                }}
              >
                <div
                  style={{
                    width: '65px',
                    height: '65px',
                    margin: '0 auto 15px',
                    borderRadius: '50%',
                    background: '#e8f7ee',
                    color: '#28a745',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '32px'
                  }}
                >
                  <i className="fas fa-check"></i>
                </div>

                <h2
                  style={{
                    marginBottom: '10px'
                  }}
                >
                  Password Reset Successful
                </h2>

                <p
                  style={{
                    color: '#666',
                    lineHeight: '1.6',
                    marginBottom: '20px'
                  }}
                >
                  Your password has been
                  successfully changed. You can now
                  login with your new password.
                </p>

                <button
                  type="button"
                  className="login-btn"
                  onClick={
                    closeForgotPassword
                  }
                >
                  <i className="fas fa-sign-in-alt"></i>
                  Go to Login
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Login;